import { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe, Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const Step1WebsiteAnalysis = ({ sessionId, onComplete }) => {
  const { t } = useLanguage();
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');

  const isValidUrl = (string) => {
    try {
      const url = new URL(string.startsWith('http') ? string : `https://${string}`);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleAnalyze = async () => {
    setError('');
    
    const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
    
    if (!isValidUrl(normalizedUrl)) {
      setError(t.step1.invalidUrl);
      return;
    }

    setIsAnalyzing(true);

    try {
      const response = await axios.post(`${API}/onboarding/analyze-company`, {
        url: normalizedUrl,
        session_id: sessionId
      });
      
      setAnalysis(response.data.analysis);
      toast.success(t.step1.analysisSuccess);
    } catch (err) {
      const message = err.response?.data?.detail || t.step1.analysisFailed;
      setError(message);
      toast.error(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleContinue = () => {
    onComplete(analysis);
  };

  // Loading state
  if (isAnalyzing) {
    return (
      <div className="text-center py-20" data-testid="step1-loading">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="inline-block mb-6"
        >
          <Loader2 className="w-12 h-12 text-[#00D9C8]" />
        </motion.div>
        <h2 className="text-2xl font-bold text-white mb-3">
          {t.step1.analyzingTitle}
        </h2>
        <p className="text-[#A0AEC0]">
          {t.step1.analyzingSubtitle}
        </p>
      </div>
    );
  }

  // Analysis result
  if (analysis) {
    const targetAudiences = analysis.targetAudiences || [];
    const marketingGoals = analysis.marketingGoals || [];
    const channelMix = analysis.channelMix || [];
    const contentThemes = analysis.contentThemes || [];
    const tonality = analysis.tonality || {};

    return (
      <div data-testid="step1-results" className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle className="w-6 h-6 text-[#00D9C8]" />
          <h2 className="text-2xl font-bold tracking-tight text-white">
            {t.step1.resultTitle}
          </h2>
        </div>
        <p className="text-[#A0AEC0] mb-6">
          {t.step1.resultSubtitle}
        </p>

        {/* Company Overview */}
        <Card className="bg-[#1A2B42] border-transparent mb-4">
          <CardContent className="p-5">
            <h3 className="text-lg font-bold text-white mb-1">
              {analysis.companyName || t.step1.yourCompany}
            </h3>
            <p className="text-sm text-[#A0AEC0] mb-3">
              {analysis.industry || t.step1.industry} {analysis.coreActivities && `• ${analysis.coreActivities}`}
            </p>
            
            {/* USPs */}
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-2">
                {t.step1.uspsLabel}
              </p>
              <div className="flex flex-wrap gap-2">
                {(analysis.usps || []).slice(0, 4).map((usp, index) => (
                  <Badge 
                    key={index} 
                    className="bg-[#0A1628] text-[#00D9C8] border-[#00D9C8]/30 px-3 py-1 text-xs"
                  >
                    {typeof usp === 'string' ? usp : usp?.name || 'USP'}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Tonality */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-1">{t.step1.languageStyle}</p>
                <p className="text-sm text-white">{tonality.style || analysis.brandVoice || 'Professionell'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-1">{t.step1.address}</p>
                <p className="text-sm text-white">{tonality.addressing || 'Sie'} • {tonality.formality || analysis.tone || 'Formell'}</p>
              </div>
            </div>

            {/* Keywords */}
            {analysis.keywords && analysis.keywords.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-2">Keywords</p>
                <div className="flex flex-wrap gap-1">
                  {analysis.keywords.slice(0, 6).map((keyword, index) => (
                    <span key={index} className="text-xs text-[#A0AEC0] bg-[#0A1628] px-2 py-1">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Target Audiences */}
        {targetAudiences.length > 0 && (
          <Card className="bg-[#1A2B42] border-transparent mb-4">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-3">
                {t.step1.targetAudiences}
              </p>
              <div className="space-y-3">
                {targetAudiences.slice(0, 2).map((audience, index) => (
                  <div key={index} className="bg-[#0A1628] p-3">
                    <p className="text-sm font-medium text-white mb-1">
                      {audience.name || `${t.step1.audience} ${index + 1}`}
                    </p>
                    <p className="text-xs text-[#A0AEC0]">
                      {audience.description || audience.demographics}
                    </p>
                    {audience.painPoints && audience.painPoints.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {audience.painPoints.slice(0, 2).map((pain, i) => (
                          <span key={i} className="text-xs text-red-400/80">• {pain}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Marketing Goals */}
        {marketingGoals.length > 0 && (
          <Card className="bg-[#1A2B42] border-transparent mb-4">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-3">
                {t.step1.marketingGoals}
              </p>
              <div className="space-y-2">
                {marketingGoals.slice(0, 3).map((goal, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-white">{goal.goal || goal}</span>
                    {goal.priority && (
                      <Badge className={`text-xs ${
                        goal.priority === 'hoch' ? 'bg-[#00D9C8]/20 text-[#00D9C8]' :
                        goal.priority === 'mittel' ? 'bg-yellow-500/20 text-yellow-500' :
                        'bg-[#5A6C7D]/20 text-[#5A6C7D]'
                      }`}>
                        {goal.priority}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Channel Mix */}
        {channelMix.length > 0 && (
          <Card className="bg-[#1A2B42] border-transparent mb-4">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-3">
                {t.step1.channelMix}
              </p>
              <div className="space-y-2">
                {channelMix.slice(0, 4).map((channel, index) => (
                  <div key={index} className="flex items-center justify-between bg-[#0A1628] p-2">
                    <span className="text-sm text-white">{channel.channel}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#A0AEC0]">{channel.frequencyPerWeek}{t.step1.perWeek}</span>
                      <Badge className={`text-xs ${
                        channel.priority === 'primär' ? 'bg-[#00D9C8]/20 text-[#00D9C8]' :
                        channel.priority === 'sekundär' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-[#5A6C7D]/20 text-[#5A6C7D]'
                      }`}>
                        {channel.priority}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Themes */}
        {contentThemes.length > 0 && (
          <Card className="bg-[#1A2B42] border-transparent mb-6">
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-wider text-[#5A6C7D] mb-3">
                {t.step1.contentThemes}
              </p>
              <div className="space-y-2">
                {contentThemes.slice(0, 3).map((theme, index) => (
                  <div key={index}>
                    <p className="text-sm font-medium text-white">{theme.mainTheme || theme}</p>
                    {theme.subTopics && theme.subTopics.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {theme.subTopics.slice(0, 3).map((sub, i) => (
                          <span key={i} className="text-xs text-[#A0AEC0]">• {sub}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-xs text-[#5A6C7D] mb-4">
          {t.step1.onBrandNote}
        </p>

        <Button 
          onClick={handleContinue}
          className="w-full h-12 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold"
          data-testid="step1-continue-btn"
        >
          {t.step1.continueBtn}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  // Initial form
  return (
    <div data-testid="step1-form">
      <h2 className="text-4xl font-black tracking-tight mb-3 text-white">
        {t.step1.title}
      </h2>
      <p className="text-[#A0AEC0] mb-8">
        {t.step1.subtitle}
      </p>

      <div className="space-y-4">
        <div className="relative">
          <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5A6C7D]" />
          <Input
            type="url"
            placeholder={t.step1.urlPlaceholder}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="pl-12 h-12 bg-[#1A2B42] border-transparent focus:border-[#00D9C8] text-white placeholder:text-[#5A6C7D]"
            data-testid="website-url-input"
          />
        </div>
        
        {error && (
          <p className="text-sm text-red-400" data-testid="step1-error">
            {error}
          </p>
        )}

        <Button 
          onClick={handleAnalyze}
          disabled={!url.trim()}
          className="w-full h-12 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold disabled:opacity-50"
          data-testid="analyze-btn"
        >
          {t.step1.analyzeBtn}
        </Button>
      </div>
    </div>
  );
};

export default Step1WebsiteAnalysis;
